/**
 * Timeline Selector Widget
 *
 * Date/timeline picker for journey planning.
 * Supports WidgetProps (onComplete) for CoPilot and legacy onSelect for direct use.
 */

import { Calendar, ChevronRight, Clock } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Card, CardContent } from '@tonnie/ui';
import { useState } from 'react';
import type { WidgetProps } from '@tonnie/copilot';

interface ITimelineOption {
  id: string;
  label: string;
  description?: string;
  months?: number;
}

interface IPreset {
  id: string;
  label: string;
  value?: string;
  description?: string;
  months?: number;
}

type ITimelineSelectorWidgetProps = WidgetProps & {
  config?: {
    title?: string;
    description?: string;
    options?: ITimelineOption[];
    presets?: IPreset[];
  };
  onSelect?: (value: string) => void;
  initialValue?: string;
};

export function TimelineSelectorWidget({
  config,
  onComplete,
  onSelect,
  initialValue,
}: ITimelineSelectorWidgetProps) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(initialValue || null);

  const defaultOptions: ITimelineOption[] = [
    {
      id: 'asap',
      label: t('widgets.timeline.asap'),
      description: t('widgets.timeline.asapDesc'),
      months: 1,
    },
    {
      id: '1-3',
      label: t('widgets.timeline.1to3months'),
      description: t('widgets.timeline.1to3monthsDesc'),
      months: 2,
    },
    {
      id: '3-6',
      label: t('widgets.timeline.3to6months'),
      description: t('widgets.timeline.3to6monthsDesc'),
      months: 4,
    },
    {
      id: '6-12',
      label: t('widgets.timeline.6to12months'),
      description: t('widgets.timeline.6to12monthsDesc'),
      months: 9,
    },
    {
      id: '12+',
      label: t('widgets.timeline.12plusMonths'),
      description: t('widgets.timeline.12plusMonthsDesc'),
      months: 18,
    },
    {
      id: 'flexible',
      label: t('widgets.timeline.flexible'),
      description: t('widgets.timeline.flexibleDesc'),
    },
  ];

  const presetsAsOptions: ITimelineOption[] | undefined = config?.presets?.map((p) => ({
    id: p.id,
    label: p.label,
    description: p.description,
    months: p.months,
  }));
  const options = config?.options || presetsAsOptions || defaultOptions;

  const handleSelect = (optionId: string) => {
    setSelectedId(optionId);
    const selected = options.find((o) => o.id === optionId);
    onComplete?.({ timeline: { plan: optionId, months: selected?.months } });
    onSelect?.(optionId);
  };

  return (
    <Card className="border-border/50 shadow-sm" data-testid="widget-timeline_selector">
      <CardContent className="pt-6">
        {config?.title && (
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-primary-accessible" />
            <h3 className="text-base font-semibold">{config.title}</h3>
          </div>
        )}
        {config?.description && (
          <p className="text-sm text-muted-foreground mb-4">{config.description}</p>
        )}

        <div className="space-y-2">
          {options.map((option) => {
            const isSelected = selectedId === option.id;
            return (
              <button
                key={option.id}
                onClick={() => handleSelect(option.id)}
                data-testid={`widget-timeline_selector-date-${option.id}`}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}
                >
                  <Clock className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-sm">{option.label}</p>
                  {option.description && (
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  )}
                </div>
                <ChevronRight
                  className={`w-5 h-5 transition-transform ${
                    isSelected ? 'text-primary-accessible translate-x-1' : 'text-muted-foreground'
                  }`}
                />
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default TimelineSelectorWidget;
